import React, { useEffect, useState } from 'react'
import { Modal } from '../UI/Modal/Modal'
import {
  useGetSampleRequest,
  useReviewSampleRequest,
} from '../../services/samples'
import type { SampleRequestDetail } from '../../models/samples'
import { SampleRequestPreview } from './SampleRequestPreview'
import { Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import to from 'await-to-js'

interface SampleRequestReviewModalProps {
  show: boolean
  onHide: () => void
  sampleRequestId: number | null
  onSuccess?: () => void
}

export function SampleRequestReviewModal({
  show,
  onHide,
  sampleRequestId,
  onSuccess,
}: SampleRequestReviewModalProps) {
  const [confirmAction, setConfirmAction] = useState<
    'accept' | 'reject' | null
  >(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const reviewMutation = useReviewSampleRequest()

  const { data: order, isLoading } = useGetSampleRequest(
    { id: sampleRequestId ?? 0 },
    { query: { enabled: !!sampleRequestId && show } }
  )

  useEffect(() => {
    if (show) {
      setConfirmAction(null)
      setRejectionReason('')
    }
  }, [sampleRequestId, show])

  const handleAcceptClick = () => {
    setConfirmAction('accept')
  }

  const handleRejectClick = () => {
    setConfirmAction('reject')
  }

  const handleConfirm = async () => {
    if (!sampleRequestId || !confirmAction) return

    setIsSubmitting(true)

    const data =
      confirmAction === 'accept'
        ? { status: 'accepted' as const }
        : {
            status: 'rejected' as const,
            rejection_reason: rejectionReason,
          }

    const [err] = await to(
      reviewMutation.mutateAsync({ id: sampleRequestId, data })
    )

    setIsSubmitting(false)

    if (!err) {
      toast.success(
        confirmAction === 'accept'
          ? 'Sample request accepted successfully'
          : 'Sample request rejected successfully'
      )
      setConfirmAction(null)
      setRejectionReason('')
      // Detail + list caches are invalidated by the mutation registry.
      onSuccess?.()
      onHide()
    } else {
      toast.error(`Failed to ${confirmAction} sample request`)
    }
  }

  const handleCancelConfirm = () => {
    setConfirmAction(null)
    setRejectionReason('')
  }

  const isPendingReview = order?.status === 'pending_review'

  return (
    <>
      <Modal
        open={show}
        onOpenChange={(open) => !open && onHide()}
        title="Review Sample Request"
        description="Review and accept or reject this sample request"
        size="lg"
        hideCancelButton={!isPendingReview}
        onAccept={isPendingReview ? handleAcceptClick : undefined}
        acceptButtonText="Accept"
        acceptButtonProps={{ variant: 'primary' }}
        onAlt={isPendingReview ? handleRejectClick : undefined}
        altButtonText="Reject"
        altButtonProps={{ variant: 'danger' }}
      >
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : order ? (
          <SampleRequestPreview
            order={order as unknown as SampleRequestDetail}
            showHeader={true}
          />
        ) : (
          <div className="text-center py-5 text-muted">
            Sample request not found
          </div>
        )}
      </Modal>

      {/* Accept Confirmation Modal */}
      <Modal
        open={confirmAction === 'accept'}
        onOpenChange={(open) => !open && handleCancelConfirm()}
        title="Confirm Accept"
        description="Confirm accepting this sample request"
        size="md"
        onAccept={handleConfirm}
        acceptButtonText="Confirm Accept"
        acceptButtonProps={{
          variant: 'primary',
          disabled: isSubmitting,
        }}
      >
        <p>
          Are you sure you want to accept this sample request? This will send
          the request to customer service for fulfillment.
        </p>
      </Modal>

      {/* Reject Confirmation Modal */}
      <Modal
        open={confirmAction === 'reject'}
        onOpenChange={(open) => !open && handleCancelConfirm()}
        title="Confirm Reject"
        description="Confirm rejecting this sample request"
        size="md"
        onAccept={handleConfirm}
        acceptButtonText="Confirm Reject"
        acceptButtonProps={{
          variant: 'danger',
          disabled: isSubmitting || !rejectionReason.trim(),
        }}
      >
        <p>
          Are you sure you want to reject this sample request? The broker will
          be notified with the reason below.
        </p>
        <textarea
          className="mt-3 w-full rounded-md border border-line-form p-3 text-sm focus:outline-none"
          rows={4}
          placeholder="Enter rejection reason (required)"
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
        />
      </Modal>
    </>
  )
}
